import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';

import { Close } from '@mui/icons-material';
import WarningOrangeImg from '@src/assets/icons/warning-orange.svg';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';

import actions from '@src/redux/actions';
import { delimitNumber } from '@src/utils/number';
import { PACKAGE_TYPE } from '@src/constants/package';
import { convertSecondsToHMS } from '@src/utils/time';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import route from '@src/constants/route';
import { DEFAULT_VOICE_CLONING_LIMIT_FOR_WHITELIST } from '@src/constants/voice';

import {
  APPLE_CANCEL_SUBSCRIPTION_URL,
  GOOGLE_CANCEL_SUBSCRIPTION_URL,
} from '@src/configs';
import PackageV3ChangeInfoPopup from '../PackageV3ChangeInfoPopup';
import { StyledNotConfirmOrderDialog } from './index.style';
import ContentPopupUnPaidOrder from '../ContentPopupUnPaidOrder';
import PopupNoticeRolloverOrder from './PopupNoticeRolloverOrder';

const PaymentNotiDialog = ({
  typeCheckUpDownGradePackV3,
  isAutoRenewing,
  handleConfirmUpDownGradePack,
  dataCharacterLimitExceeded,
  handleConfirmPaymentTopup,
  isVisiblePaymentDialog,
  handleConfirmPaymentDialogCheckbox,
  dataConfirmPaymentDialog,
  voiceCloningQuota,
  openDialog,
  DIALOG_NAMES,
  onCloseDialog,
  onComeBackToPackage,
  onConfirmDowngradePackage,
  onConfirmDowngradePackageAndCancelSubscription,
  onConfirmUpgradeOrBuyMore,
  onContinuePaying,
  onRedirectToTTS,
  isDubbingRoute,
  productInfo = {},
  paymentMethodType,
  orderPackageInfoWhenApplyVoucher,
  creditsOrderData,
  hasRedeemCode,
  onPaymentWithRedeemCode,
  onOpenRedeemDialog,
  isLoading,
}) => {
  const { t, language } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { latestOrder, latestDubbingOrder } = useSelector(
    (state) => state.user,
  );
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();

  const activeDialogName = Object.keys(openDialog).find(
    (key) => openDialog[key],
  );

  const paymentMethod = useMemo(() => {
    switch (paymentMethodType) {
      case PAYMENT_METHOD_TYPE.APPLE:
        return 'Apple';
      case PAYMENT_METHOD_TYPE.GOOGLE:
        return 'Google';
      default:
        return paymentMethodType;
    }
  }, [paymentMethodType]);

  const latestOrderData = isDubbingRoute ? latestDubbingOrder : latestOrder;
  const isNapasPendingOrder = [
    PAYMENT_METHOD_TYPE.NAPAS_CARD,
    PAYMENT_METHOD_TYPE.NAPAS_ACCOUNT,
  ].includes(latestOrderData?.paymentMethod?.type);

  const redirectLink = useMemo(() => {
    switch (paymentMethodType) {
      case PAYMENT_METHOD_TYPE.APPLE:
        return APPLE_CANCEL_SUBSCRIPTION_URL;
      case PAYMENT_METHOD_TYPE.GOOGLE:
        return GOOGLE_CANCEL_SUBSCRIPTION_URL;
      default:
        return '';
    }
  }, [paymentMethodType]);

  const ACTION_TYPE = {
    DOWNGRADE: 'downgrade',
    UPGRADE_OR_BUY_MORE: 'upgradeOrBuyMore',
  };

  const generateContent = (actionType) => {
    const { total, expires, packageType } = productInfo;
    const formatExpires = moment(expires).format('DD/MM/YYYY');

    const messageKeys = {
      [ACTION_TYPE.DOWNGRADE]: {
        studio: 'warningDowngradePackageDescriptionStudio',
        dubbing: 'warningDowngradePackageDescriptionDubbing',
      },
      [ACTION_TYPE.UPGRADE_OR_BUY_MORE]: {
        studio: 'warningUpgradePackageOrBuyMoreDescriptionStudio',
        dubbing: 'warningUpgradePackageOrBuyMoreDescriptionDubbing',
      },
    };

    if (packageType === PACKAGE_TYPE.STUDIO) {
      const formatTotal = delimitNumber(total, language);
      return t(messageKeys[actionType].studio, {
        formatTotal,
        formatExpires,
      });
    }

    const formatTotal = convertSecondsToHMS(total || 0);
    return t(messageKeys[actionType].dubbing, {
      formatTotal,
      formatExpires,
    });
  };

  // TODO: Refactor this function
  const VoiceCloningQuotaNotice = () => {
    const isVoiceCloningWhitelisted = getFeatureValue(
      FEATURE_KEYS.VOICE_CLONING,
      {
        userId: user?.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
      },
    );

    const {
      maxCloningVoices = 0,
      maxInstantVoiceCloning = 0,
      currentVoiceCloningCount = 0,
      currentInstantVoiceCount = 0,
    } = voiceCloningQuota || {};

    const hasNewQuota = isVoiceCloningWhitelisted || maxCloningVoices > 0;

    return (
      <span className="voice-cloning-quota-notice">
        {t('voiceCloningQuotaChangeDesc')}
        <br />
        <ul>
          <li>
            <strong>{t('youHave')}:</strong>{' '}
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t('currentVoiceCloningQuota', {
                    currentVoiceCloningCount,
                    currentInstantVoiceCount,
                  }),
                ),
              }}
            />
          </li>
          <li>
            <strong>{t('newPlanRule')}:</strong>{' '}
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  hasNewQuota
                    ? t('newVoiceCloningQuota', {
                        maxCloningVoices:
                          maxCloningVoices ||
                          DEFAULT_VOICE_CLONING_LIMIT_FOR_WHITELIST,
                        maxInstantVoiceCloning,
                      })
                    : t('noNewVCQuota', {
                        maxInstantVoiceCloning,
                      }),
                ),
              }}
            />
          </li>
        </ul>
        {t('voiceCloningQuotaGuide')}
      </span>
    );
  };

  const dialogList = [
    {
      name: DIALOG_NAMES.UPGRADE_DOWNGRADE_PACK_V3,
      title: t('noticePopup'),
      content: (
        <PackageV3ChangeInfoPopup
          typeCheckUpDownGradePackV3={typeCheckUpDownGradePackV3}
          isAutoRenewing={isAutoRenewing}
          orderPackageInfoWhenApplyVoucher={orderPackageInfoWhenApplyVoucher}
        />
      ),
    },
    {
      name: DIALOG_NAMES.ROLLOVER_ORDER,
      title: t('noticePopup'),
      content: <PopupNoticeRolloverOrder creditsOrderData={creditsOrderData} />,
    },
    {
      name: DIALOG_NAMES.NOT_CONFIRMED_ORDER,
      title: t('havePendingOrderTitle'),
      content: (
        <ContentPopupUnPaidOrder
          isNotConfirmedOrder
          isNapasOrder={isNapasPendingOrder}
        />
      ),
    },
    {
      name: DIALOG_NAMES.DOWNGRADE_PACKAGE,
      title: t('warningSelectSmallerPackageTitle'),
      description: generateContent(ACTION_TYPE.DOWNGRADE),
    },
    {
      name: DIALOG_NAMES.UPGRADE_PACKAGE_OR_BUY_MORE,
      title: t('warningSelectSmallerPackageTitle'),
      description: generateContent(ACTION_TYPE.UPGRADE_OR_BUY_MORE),
    },
    {
      name: DIALOG_NAMES.DOWNGRADE_PACKAGE_AND_CANCEL_SUBSCRIPTION,
      title: t('warningSelectSmallerPackageTitle'),
      description: t('downgradePackageAndCancelSubscriptionDescription'),
    },
    {
      name: DIALOG_NAMES.CANCEL_SUBSCRIPTION,
      title: t('cancelSubscriptionTitle'),
      description: t('cancelSubscriptionDescription'),
    },
    {
      name: DIALOG_NAMES.UNPAID_ORDER,
      title: t('havePendingOrderTitle'),
      content: <ContentPopupUnPaidOrder isNapasOrder={isNapasPendingOrder} />,
    },
    {
      name: DIALOG_NAMES.COMPLETED_ORDER,
      title: t('completedOrderTitle'),
      description: t('completedTransactionDescription'),
    },
    {
      name: DIALOG_NAMES.WARNING_EMAIL_ERROR,
      title: t('warning'),
      description: t('warningEmailError'),
    },
    {
      name: DIALOG_NAMES.INVALID_REGION_VOUCHER,
      title: t('invalidVoucher'),
      description: t('notValidVoucherTypeInCountry'),
    },
    {
      name: DIALOG_NAMES.BUY_SUBSCRIPTION_ON_APP,
      title: t('cannotBuyPackage'),
      description: t('cancelSubscriptionOnAppDescription', { paymentMethod }),
    },
    {
      name: DIALOG_NAMES.CANCEL_SUBSCRIPTION_ON_APP,
      title: t('cancelAutoRenewGuide'),
      description: t('cancelAutoRenewGuideDescriptionOnApp', { paymentMethod }),
    },
    {
      name: DIALOG_NAMES.CONFIRM_PAYMENT,
      title: t('confirmPayment'),
      description: t('descriptionConfirmPaymentV2', {
        numberCharacters: dataConfirmPaymentDialog?.numberCharacters,
        hour: dataConfirmPaymentDialog?.hour,
        dateExpired: dataConfirmPaymentDialog?.dateExpired,
        remainingDays: dataConfirmPaymentDialog?.remainingDays + 1,
      }),
    },
    {
      name: DIALOG_NAMES.EXCEEDS_CHARACTERS,
      title: t('CharacterLimitExceeded'),
      description: t('descriptionExceededCharacters', {
        maxCharacters: dataCharacterLimitExceeded?.maxCharacters,
        currentCharacters: dataCharacterLimitExceeded?.currentCharacters,
      }),
    },
    {
      name: DIALOG_NAMES.VOICE_CLONING_QUOTA,
      title: t('noticePopup'),
      content: <VoiceCloningQuotaNotice />,
    },
  ];

  const activeDialog = dialogList.find(
    (dialog) => activeDialogName && dialog.name === activeDialogName,
  );

  const handleCancelPendingOrder = async () => {
    dispatch(
      actions.cancelReasonDialog.showCancelReasonDialog({
        orderId: latestOrder.id,
      }),
    );
  };

  const handleGoBack = () => {
    history.push(route.PAYMENT);
    if (hasRedeemCode) {
      onCloseDialog(activeDialogName);
      onOpenRedeemDialog();
    }
  };

  const handleCloseDialogRolloverOrder = () => {
    if (hasRedeemCode) {
      onPaymentWithRedeemCode();
    } else {
      onCloseDialog(activeDialogName);
    }
  };

  const handleCloseDialogCancelSubscription = () => {
    if (hasRedeemCode) {
      history.push(route.PAYMENT);
      onCloseDialog(DIALOG_NAMES.CANCEL_SUBSCRIPTION);
      onOpenRedeemDialog();
    } else {
      onCloseDialog(DIALOG_NAMES.CANCEL_SUBSCRIPTION);
    }
  };

  const getButtonContentRolloverOrder = () => {
    if (hasRedeemCode) {
      if (isLoading) {
        return (
          <CircularProgress
            value={75}
            className="circular-progress"
            size="24px"
          />
        );
      }
      return t('confirm');
    }
    return t('continue');
  };

  const renderNotiAction = () => {
    switch (activeDialogName) {
      case DIALOG_NAMES.UNPAID_ORDER:
        return (
          <Box className="actions-pending-orders">
            <Button
              className="action-button base-style-pending-order cancel-button"
              onClick={handleCancelPendingOrder}
              data-id="button-cancel-order"
              data-order-id={
                isDubbingRoute ? latestDubbingOrder?.id : latestOrder.id
              }
            >
              {t('cancelOrder')}
            </Button>
            {!isNapasPendingOrder && (
              <Button
                variant="contained"
                className="action-button base-style-pending-order pending-button"
                onClick={() => onContinuePaying()}
                data-id="button-continue-payment"
                data-order-id={
                  isDubbingRoute ? latestDubbingOrder?.id : latestOrder.id
                }
              >
                {t('continuePayment')}
              </Button>
            )}
          </Box>
        );
      case DIALOG_NAMES.NOT_CONFIRMED_ORDER:
        return (
          <Box className="actions-pending-orders">
            <Button
              className="action-button base-style-pending-order cancel-button"
              onClick={handleCancelPendingOrder}
              data-id="button-cancel-order"
              data-order-id={
                isDubbingRoute ? latestDubbingOrder?.id : latestOrder.id
              }
            >
              {t('cancelOrder')}
            </Button>
            {!isNapasPendingOrder && (
              <Button
                variant="contained"
                className="action-button base-style-pending-order pending-button"
                onClick={() => onComeBackToPackage()}
                data-id="button-awaiting-approval"
                data-order-id={
                  isDubbingRoute ? latestDubbingOrder?.id : latestOrder.id
                }
              >
                {t('pendingActivation')}
              </Button>
            )}
          </Box>
        );

      case DIALOG_NAMES.COMPLETED_ORDER:
        return (
          <Box className="action-wrapper">
            <Button
              variant="contained"
              className="action-button pending-button"
              onClick={onRedirectToTTS}
            >
              {t('completed')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.DOWNGRADE_PACKAGE:
        return (
          <Box className="action-wrapper">
            <Button
              className="action-button cancel-button"
              onClick={() => onCloseDialog(DIALOG_NAMES.DOWNGRADE_PACKAGE)}
              data-id="close-checkout"
            >
              {t('chooseAnotherPackage')}
            </Button>
            <Button
              variant="contained"
              className="action-button pending-button"
              onClick={onConfirmDowngradePackage}
              data-id="confirm-checkout"
            >
              {t('continue')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.UPGRADE_PACKAGE_OR_BUY_MORE:
        return (
          <Box className="action-wrapper">
            <Button
              className="action-button cancel-button"
              onClick={() =>
                onCloseDialog(DIALOG_NAMES.UPGRADE_PACKAGE_OR_BUY_MORE)
              }
            >
              {t('chooseAnotherPackage')}
            </Button>
            <Button
              variant="contained"
              className="action-button pending-button"
              onClick={onConfirmUpgradeOrBuyMore}
            >
              {t('continue')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.DOWNGRADE_PACKAGE_AND_CANCEL_SUBSCRIPTION:
        return (
          <Box className="action-wrapper">
            <Button
              className="action-button cancel-button"
              onClick={() =>
                onCloseDialog(
                  DIALOG_NAMES.DOWNGRADE_PACKAGE_AND_CANCEL_SUBSCRIPTION,
                )
              }
            >
              {t('chooseAnotherPackage')}
            </Button>
            <Button
              variant="contained"
              className="action-button pending-button"
              onClick={onConfirmDowngradePackageAndCancelSubscription}
            >
              {t('continue')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.CANCEL_SUBSCRIPTION:
        return (
          <Box className="action-wrapper action-wrapper-understood">
            <Button
              variant="contained"
              className="action-button pending-button understood-button"
              onClick={handleCloseDialogCancelSubscription}
            >
              {t('gotIt')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.WARNING_EMAIL_ERROR:
        return (
          <Box className="action-wrapper action-wrapper-understood">
            <Button
              variant="contained"
              className="action-button pending-button understood-button"
              onClick={() => onCloseDialog(DIALOG_NAMES.WARNING_EMAIL_ERROR)}
            >
              {t('understood')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.INVALID_REGION_VOUCHER:
        return (
          <Box className="action-wrapper action-wrapper-understood">
            <Button
              variant="contained"
              className="action-button pending-button understood-button"
              onClick={() => onCloseDialog(DIALOG_NAMES.INVALID_REGION_VOUCHER)}
            >
              {t('understoodV2')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.BUY_SUBSCRIPTION_ON_APP:
      case DIALOG_NAMES.CANCEL_SUBSCRIPTION_ON_APP:
        return (
          <Box className="action-wrapper action-wrapper-understood">
            <Button
              variant="contained"
              className="action-button pending-button understood-button"
              onClick={() => {
                onCloseDialog(activeDialogName);
                window.open(redirectLink, '_blank', 'noopener,noreferrer');
              }}
            >
              {t('viewTheGuide')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.CONFIRM_PAYMENT:
        return (
          <div className="action-confirm-payment">
            <FormControlLabel
              className="check-box-not-show-again"
              control={
                <Checkbox
                  classes={{ root: 'checkbox' }}
                  color="iconPrimary"
                  value={isVisiblePaymentDialog}
                  onChange={handleConfirmPaymentDialogCheckbox}
                />
              }
              label={
                <Typography className="not-show-again-text">
                  {t('notShowAgain')}
                </Typography>
              }
              labelPlacement={t('notShowAgain')}
            />
            <div className="action-btn">
              <Button
                onClick={() => onCloseDialog(activeDialogName)}
                className="action-button-outline"
                variant="outlined"
              >
                {t('notNow')}
              </Button>
              <Button
                onClick={handleConfirmPaymentTopup}
                className="action-button-contained"
                variant="contained"
              >
                {t('next')}
              </Button>
            </div>
          </div>
        );

      case DIALOG_NAMES.EXCEEDS_CHARACTERS:
        return (
          <Box className="action-wrapper action-wrapper-understood">
            <Button
              variant="contained"
              className="action-button-contained"
              onClick={() => onCloseDialog(activeDialogName)}
            >
              {t('gotIt')}
            </Button>
          </Box>
        );

      case DIALOG_NAMES.UPGRADE_DOWNGRADE_PACK_V3:
        return (
          <div className="action-confirm-updowngrade">
            <div />
            <div>
              <div className="action-btn">
                <Button
                  onClick={() => onCloseDialog(activeDialogName)}
                  className="action-button-outline"
                  variant="outlined"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleConfirmUpDownGradePack}
                  className="action-button-contained"
                  variant="contained"
                  data-id="button-confirm-payment"
                >
                  {t('agreeToPay')}
                </Button>
              </div>
            </div>
          </div>
        );
      case DIALOG_NAMES.ROLLOVER_ORDER:
        return (
          <div className="action-confirm-updowngrade">
            <div />
            <div>
              <div className="action-btn">
                <Button
                  onClick={handleGoBack}
                  className="action-button-outline"
                  variant="outlined"
                >
                  {t('goBack')}
                </Button>
                <Button
                  onClick={handleCloseDialogRolloverOrder}
                  className="action-button-contained"
                  variant="contained"
                  data-id="button-confirm-payment"
                  disabled={isLoading}
                >
                  {getButtonContentRolloverOrder()}
                </Button>
              </div>
            </div>
          </div>
        );

      case DIALOG_NAMES.VOICE_CLONING_QUOTA:
        return (
          <Box className="action-wrapper action-wrapper-understood">
            <Button
              variant="outlined"
              className="new-action-button understood-button width-auto cancel-button"
              onClick={() => history.push(route.VOICE_CLONING_VOICES)}
            >
              {t('manageVoices')}
            </Button>
            <Button
              variant="contained"
              className="new-action-button pending-button understood-button width-auto"
              onClick={() => history.push(route.PAYMENT)}
            >
              {t('choosePlan')}
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  const onClickCloseDialog = () => {
    if (hasRedeemCode) {
      history.push(route.PAYMENT);
      onCloseDialog(activeDialog.name);
      onOpenRedeemDialog();
    } else {
      onCloseDialog(activeDialog.name);
      if (
        [DIALOG_NAMES.UNPAID_ORDER, DIALOG_NAMES.NOT_CONFIRMED_ORDER].includes(
          activeDialog.name,
        )
      ) {
        history.push(route.PAYMENT);
      }
    }
  };

  const renderNotiContent = () => (
    <>
      <Box className="title-wrapper ">
        <Box className="title-container">
          {[
            DIALOG_NAMES.INVALID_REGION_VOUCHER,
            DIALOG_NAMES.EXCEEDS_CHARACTERS,
            DIALOG_NAMES.VOICE_CLONING_QUOTA,
          ].includes(activeDialog.name) && (
            <img src={WarningOrangeImg} alt="warning" />
          )}
          <Typography className="title-content">
            {activeDialog.title}
          </Typography>
        </Box>

        {[
          DIALOG_NAMES.INVALID_REGION_VOUCHER,
          DIALOG_NAMES.CONFIRM_PAYMENT,
          DIALOG_NAMES.EXCEEDS_CHARACTERS,
          DIALOG_NAMES.UPGRADE_DOWNGRADE_PACK_V3,
          DIALOG_NAMES.VOICE_CLONING_QUOTA,
          DIALOG_NAMES.NOT_CONFIRMED_ORDER,
          DIALOG_NAMES.UNPAID_ORDER,
        ].includes(activeDialog.name) && (
          <IconButton onClick={onClickCloseDialog}>
            <Close />
          </IconButton>
        )}
      </Box>
      <Divider />
      <Box className="content-wrapper">
        {activeDialog?.content ? (
          activeDialog?.content
        ) : (
          <Typography
            className="content"
            dangerouslySetInnerHTML={{
              __html: activeDialog.description,
            }}
          />
        )}
      </Box>
    </>
  );

  const renderNotiContentRolloverOrder = () => (
    <>
      <Box className="title-wrapper ">
        <Box className="title-container">
          <Typography className="title-content">
            {activeDialog.title}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="content-wrapper">{activeDialog?.content}</Box>
    </>
  );

  if (!activeDialog) return '';

  return (
    <StyledNotConfirmOrderDialog
      open={openDialog[activeDialog.name]}
      fullWidth
      maxWidth="sm"
      dialogName={activeDialog.name}
      onClose={() => {
        if (
          [
            DIALOG_NAMES.UNPAID_ORDER,
            DIALOG_NAMES.NOT_CONFIRMED_ORDER,
            DIALOG_NAMES.ROLLOVER_ORDER,
          ].includes(activeDialog.name)
        ) {
          return;
        }
        onCloseDialog(activeDialog?.name);
      }}
    >
      <Box className="wrapper">
        {activeDialog.name === DIALOG_NAMES.ROLLOVER_ORDER
          ? renderNotiContentRolloverOrder()
          : renderNotiContent()}
        {renderNotiAction()}
      </Box>
    </StyledNotConfirmOrderDialog>
  );
};

export default PaymentNotiDialog;
